import useTranslation from "next-translate/useTranslation"

import { IProject, ProjectProgress, SelfAssessment } from "@api/schema"
import { IconName } from "@components/common/Icon"
import { pageChallengeSelectionIsVisibleAndAccessible, pageProjectPlanIsAccessible, pageProposalOverviewIsVisibleAndAccessible } from "@services/projectHelper"
import { buildHashStringForProposal } from "@services/proposalHelper"
import { Routes, routeWithParams } from "@services/routes"
import { MODULE_CHALLENGE_AVAILABLE, MODULE_FUNDS_AVAILABLE } from "config"

import ProgressBox from "../../common/ProgressBox"
import ProgressSubBox from "./ProgressSubBox"
// import SubmissionBox from "./SubmissionBox"


interface IProps {
  project: IProject
}

/**
 * this component provides the workflow of a single project
 * and its ActionIcons for the status of a project
 * for the project team & project-management
 *
 * @todo: DresdenFeatures: Rework conditions, if a workflow-card is shown
 */
const ProjectWorkflow: React.FC<IProps> = (props: IProps) => {
  const { t } = useTranslation("common")
  const { project } = props

  const activeProposal = project?.proposals?.find(proposal => proposal.active)

  // determine, if any module challenge icons should be displayed
  const challengeIcons: IconName[] = []
  if (MODULE_CHALLENGE_AVAILABLE) {
    challengeIcons.push("challenge")
  }
  if (MODULE_FUNDS_AVAILABLE) {
    challengeIcons.push("money-bag")
  }


  return <>
    <div className="workflow">
      <ProgressBox
        complete={true}
        icon="light-bulb"
        title="project.tableau.idea.title"
        subtitle="project.tableau.common.project"
        progress={100}
        id={"idea_box" + project.slug}
        tooltipText={t("project.tableau.idea.tooltip")}
      />

      <ProgressBox
        complete={project.profileSelfAssessment === SelfAssessment.Complete}
        icon="document"
        title="project.tableau.profile.title"
        subtitle="project.tableau.common.project"
        linkTo={routeWithParams(Routes.ProjectProfile, { slug: project.slug || project.id })}
        progress={project.profileSelfAssessment}
        id={"profile_box" + project.slug}
        tooltipText={t("project.tableau.profile.tooltip")}
      />
      <ProgressBox
        icon="compasses"
        title="project.tableau.plan.title"
        subtitle="project.tableau.common.project"
        complete={project.progress !== ProjectProgress.CreatingProfile && project.planSelfAssessment === SelfAssessment.Complete}
        active={pageProjectPlanIsAccessible(project)}
        progress={project.planSelfAssessment}
        linkTo={routeWithParams(Routes.ProjectPlan, { slug: project.slug || project.id })}
        id={"plan_box" + project.slug}
        tooltipText={project.progress !== ProjectProgress.CreatingProfile
          ? t("project.tableau.plan.tooltip")
          // @todo: check other inactive reasons
          : t("project.tableau.common.inactive")
        }
      />
      {(MODULE_CHALLENGE_AVAILABLE || MODULE_FUNDS_AVAILABLE) && <>
        {pageProposalOverviewIsVisibleAndAccessible(project)
          ? <ProgressBox
            icon={challengeIcons}
            title="project.tableau.proposalOverview.title"
            subtitle="project.tableau.proposalOverview.subtitle"
            active={true}
            linkTo={routeWithParams(Routes.ProjectProposals, { slug: project.slug || project.id }) + buildHashStringForProposal(activeProposal)}
            id={"challenge_box" + project.slug}
            tooltipText={project.progress !== ProjectProgress.CreatingProfile
              ? t("project.tableau.proposalOverview.tooltip.accessible")
              // @todo: check other inactive reasons
              : t("project.tableau.common.inactive")
            }
            showCheck={false}
          />
          : <ProgressBox
            icon={challengeIcons}
            title="project.tableau.selectChallenge.title"
            subtitle="project.tableau.selectChallenge.subtitle"
            active={pageChallengeSelectionIsVisibleAndAccessible(project)}
            linkTo={routeWithParams(Routes.ProjectSelectChallenge, { slug: project.slug || project.id })}
            id={"challenge_box" + project.slug}
            tooltipText={project.progress !== ProjectProgress.CreatingProfile
              ? t("project.tableau.selectChallenge.tooltip.accessible")
              // @todo: check other inactive reasons
              : t("project.tableau.common.inactive")
            }
            showCheck={false}
          />}
      </>}
    </div>

    <div className="sub-workflow">
      <ProgressSubBox href={Routes.Pubtools} title={t("project.tableau.pubtools")} />
    </div>
  </>
}

export default ProjectWorkflow
